import React, { useState } from "react";
import { PageHeader } from "@/components/common/PageHeader";
import { useApp } from "@/context/AppContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Sparkle, Copy, ArrowClockwise, Check } from "@phosphor-icons/react";
import { toast } from "sonner";

const CONTENT_TYPES = ["Social caption", "Reel script", "Carousel copy", "Product description", "Blog post", "Meta ad copy", "Google ad headlines", "Email subject line", "Email body", "WhatsApp message", "Press release", "Launch announcement"];
const TONES = ["Playful & premium", "Bold & energetic", "Elegant & minimal", "Formal", "Witty Gen-Z"];
const LENGTHS = ["Short", "Medium", "Long"];

export default function AIContentStudio() {
  const { brands } = useApp();
  const [form, setForm] = useState({ content_type: "Social caption", brand: "", product: "", audience: "Gen Z fashion lovers", platform: "Instagram", tone: "Playful & premium", length: "Medium", language: "English", keywords: "", cta: "" });
  const [output, setOutput] = useState("");
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const generate = async () => {
    setLoading(true);
    setOutput("");
    try {
      const { data } = await api.post("/ai/generate", form);
      setOutput(data.output);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Generation failed");
    } finally {
      setLoading(false);
    }
  };

  const copy = () => {
    navigator.clipboard.writeText(output);
    setCopied(true);
    toast.success("Copied to clipboard");
    setTimeout(() => setCopied(false), 1500);
  };

  const field = (label, k, opts) => (
    <div className="space-y-1.5">
      <Label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">{label}</Label>
      {opts ? (
        <select data-testid={`studio-${k}`} value={form[k]} onChange={(e) => set(k, e.target.value)} className="flex h-10 w-full rounded-xl border border-input bg-surface-raised px-3 text-sm">
          {k === "brand" && <option value="">All brands</option>}
          {opts.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      ) : (
        <Input data-testid={`studio-${k}`} value={form[k]} onChange={(e) => set(k, e.target.value)} className="rounded-xl" />
      )}
    </div>
  );

  return (
    <div>
      <PageHeader title="AI Content Studio" subtitle="On-brand marketing copy in seconds — powered by Claude" icon={Sparkle} />
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="rounded-2xl border border-border bg-surface-raised p-6">
          <div className="grid grid-cols-2 gap-4">
            {field("Content type", "content_type", CONTENT_TYPES)}
            {field("Brand", "brand", brands.map((b) => b.name))}
            {field("Platform", "platform", ["Instagram", "Facebook", "YouTube", "Pinterest", "LinkedIn", "X", "Email", "Website"])}
            {field("Tone", "tone", TONES)}
            {field("Length", "length", LENGTHS)}
            {field("Language", "language", ["English", "Hindi", "Hinglish", "Arabic", "French"])}
          </div>
          <div className="mt-4 space-y-4">
            <div className="space-y-1.5">
              <Label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Product / context</Label>
              <Textarea data-testid="studio-product" value={form.product} onChange={(e) => set("product", e.target.value)} placeholder="e.g. New high-rise straight-leg denim, summer drop" className="rounded-xl" />
            </div>
            {field("Audience", "audience")}
            <div className="grid grid-cols-2 gap-4">
              {field("Keywords", "keywords")}
              {field("Call to action", "cta")}
            </div>
          </div>
          <Button onClick={generate} disabled={loading} data-testid="studio-generate" className="mt-5 w-full rounded-full bg-gradient-to-r from-brand-violet to-brand-pink py-6 text-white">
            <Sparkle size={18} weight="fill" className="mr-2" /> {loading ? "Generating…" : "Generate content"}
          </Button>
        </div>

        <div className="rounded-2xl border border-border bg-surface p-6">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="font-display text-lg font-bold">Output</h2>
            {output && (
              <div className="flex gap-2">
                <button onClick={generate} className="rounded-full border border-border p-2 hover:bg-secondary" data-testid="studio-regenerate"><ArrowClockwise size={16} /></button>
                <button onClick={copy} className="rounded-full border border-border p-2 hover:bg-secondary" data-testid="studio-copy">{copied ? <Check size={16} className="text-lime-400" /> : <Copy size={16} />}</button>
              </div>
            )}
          </div>
          {loading ? (
            <div className="space-y-2">
              {[...Array(5)].map((_, i) => <div key={i} className="h-4 animate-pulse rounded bg-secondary" style={{ width: `${90 - i * 8}%` }} />)}
            </div>
          ) : output ? (
            <pre data-testid="studio-output" className="whitespace-pre-wrap font-sans text-sm leading-relaxed">{output}</pre>
          ) : (
            <div className="flex h-64 flex-col items-center justify-center text-center text-muted-foreground">
              <Sparkle size={40} weight="duotone" className="mb-3 text-brand-violet" />
              <p className="text-sm">Fill in the brief and hit generate.<br />Your AI-crafted copy appears here.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
